import React, { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import SaveSearchButton from "@/components/common/SaveSearchButton";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import ListingGrid from "@/components/listings/ListingGrid";
import SchemaFilterRail from "@/components/search/SchemaFilterRail";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import CategoryIcon from "@/components/common/CategoryIcon";
import { SEARCH } from "@/constants/testIds";
import { useCategories } from "@/hooks/useCategories";
import { X, MapPin, Loader2 } from "lucide-react";
import PropertyRefine from "@/components/search/PropertyRefine";
import QuickChips from "@/components/search/QuickChips";
import Pagination from "@/components/listings/Pagination";
import SortDropdown from "@/components/listings/SortDropdown";

const PAGE_SIZE = 24;

// Every non-`attr.*` filter param known to the schema. Anything in this
// list is passed straight through to /classifieds/listings.
const PASSTHROUGH_KEYS = [
  "q", "category", "region", "city", "area",
  "min_price", "max_price",
  "condition", "sort", "lat", "lng", "open_home",
  "min_land_area", "max_land_area", "min_floor_area", "max_floor_area",
  "make", "model", "min_year", "max_year",
  "seller_type", "bg_check", // Iter-531
];

// Cap on how many attribute chips we splice into the SEO title before
// truncating with an ellipsis — keeps <title> readable on SERP and social
// share cards.
const MAX_TITLE_TOKENS = 4;

// Compose a search-page SEO string from active URL params. Called for
// both the <title> and the og:description so social share previews match
// the tab title verbatim. `total` is only appended when the user has
// applied at least one refinement beyond a bare category (otherwise the
// category landing title reads clean, e.g. "Property · Allsale Classified"
// instead of "Property · 0 listings · Allsale Classified").
function composeSeoTitle(params, categoryName, total) {
  const parts = [];
  const q = params.get("q");
  const make = params.get("make");
  const model = params.get("model");
  const minYear = params.get("min_year");
  const maxYear = params.get("max_year");
  const region = params.get("region");
  const city = params.get("city");
  const condition = params.get("condition");
  const minPrice = params.get("min_price");
  const maxPrice = params.get("max_price");

  // 1. Search query wins (most specific intent).
  if (q) {
    parts.push(`"${q}"`);
    if (categoryName) parts.push(`in ${categoryName}`);
  } else {
    // 2. Make + Model + Category composition (vehicles).
    if (make) parts.push(make);
    if (model) parts.push(model);
    if (categoryName) parts.push(categoryName);
    // 3. Year range.
    if (minYear && maxYear) parts.push(`${minYear}–${maxYear}`);
    else if (minYear) parts.push(`from ${minYear}`);
    else if (maxYear) parts.push(`up to ${maxYear}`);
  }

  // 4. Attribute chips (Body Style, Fuel Type, etc.) — cap to keep title
  // scannable. `attr.<Key>=v1,v2` splits into individual value tokens.
  const attrTokens = [];
  params.forEach((v, k) => {
    if (!k.startsWith("attr.") || !v) return;
    v.split(",").forEach((val) => {
      if (val.trim()) attrTokens.push(val.trim());
    });
  });
  if (condition) attrTokens.unshift(condition.split(",")[0]);
  if (attrTokens.length) {
    parts.push(attrTokens.slice(0, MAX_TITLE_TOKENS).join(" · ") + (attrTokens.length > MAX_TITLE_TOKENS ? "…" : ""));
  }

  // 5. Location suffix (last so it reads naturally).
  const locBits = [city, region].filter(Boolean);
  if (locBits.length) parts.push(`in ${locBits.join(", ")}`);

  // 6. Price range as a soft qualifier.
  if (minPrice && maxPrice) parts.push(`$${minPrice}–$${maxPrice}`);
  else if (minPrice) parts.push(`from $${minPrice}`);
  else if (maxPrice) parts.push(`under $${maxPrice}`);

  if (parts.length === 0) return "Browse classifieds";
  // Capitalise first meaningful token so titles read naturally
  // ("Toyota corolla cars" → "Toyota corolla cars"). Keep the rest as-is.
  const joined = parts.join(" ").replace(/\s+/g, " ").trim();
  const capitalised = joined.charAt(0).toUpperCase() + joined.slice(1);

  // Suppress the listings-count suffix on bare category (or empty) pages —
  // keeps landing titles like "Property · Allsale Classified" clean.
  const hasRefinement = !!(
    q ||
    make ||
    model ||
    minYear ||
    maxYear ||
    region ||
    city ||
    condition ||
    minPrice ||
    maxPrice ||
    attrTokens.length
  );
  if (typeof total === "number" && hasRefinement) {
    return `${capitalised} · ${total} listing${total === 1 ? "" : "s"}`;
  }
  return capitalised;
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const { data: categories = [] } = useCategories();
  const categorySlug = params.get("category") || "";
  // Iter-396 — page rides in the URL (shareable, back-button friendly).
  const page = Math.max(1, parseInt(params.get("page"), 10) || 1);

  // Build the request-params object for /listings. Every URL param that
  // Iter-238 recognises rides along verbatim, plus every `attr.<Key>` param
  // for schema-driven attribute filters.
  const listingsParams = useMemo(() => {
    const p = { country: "NZ", limit: PAGE_SIZE, page };
    PASSTHROUGH_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) p[k] = v;
    });
    params.forEach((v, k) => {
      if ((k.startsWith("attr.") || k.startsWith("attrmin.") || k.startsWith("attrmax.")) && v) p[k] = v;
    });
    // Iter-356 — sort=distance REQUIRES lat+lng (backend 400s otherwise).
    // A shared/bookmarked link missing coords falls back to the default sort.
    if (p.sort === "distance" && (!p.lat || !p.lng)) delete p.sort;
    // Iter-372 — open_home only accepts yes|weekend (anything else → 422).
    if (p.open_home && p.open_home !== "yes" && p.open_home !== "weekend") delete p.open_home;
    return p;
  }, [params, page]);

  const fetch = useQuery({
    queryKey: ["search", listingsParams],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/listings", {
        params: listingsParams,
      });
      return data;
    },
    // v5 — keep the previous grid on screen while the next page loads.
    placeholderData: (prev) => prev,
  });

  const applyFilters = (patch) => {
    const next = new URLSearchParams(params);
    // Iter-396 — any filter change resets to page 1.
    next.delete("page");
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v == null) next.delete(k);
      else next.set(k, v);
    });
    setParams(next, { replace: false });
  };

  // Iter-396 — page change updates the URL and scrolls to top.
  const goToPage = (n) => {
    const next = new URLSearchParams(params);
    if (n <= 1) next.delete("page");
    else next.set("page", String(n));
    setParams(next, { replace: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Iter-356 — contextual nearest-first sorting for listings. Location is
  // requested ONLY when the user taps the button; denial keeps the current
  // sort with a quiet note.
  const [geoState, setGeoState] = useState("idle"); // idle | locating | denied
  const nearestActive =
    params.get("sort") === "distance" && !!params.get("lat") && !!params.get("lng");
  // Iter-372 — open-home filter (Property category only)
  const openHome = params.get("open_home") || "";
  const isPropertyCat = (params.get("category") || "").startsWith("property");
  const sortNearest = () => {
    if (!navigator.geolocation) {
      setGeoState("denied");
      return;
    }
    setGeoState("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoState("idle");
        applyFilters({
          sort: "distance",
          lat: pos.coords.latitude.toFixed(5),
          lng: pos.coords.longitude.toFixed(5),
        });
      },
      () => setGeoState("denied"),
      { timeout: 10_000, maximumAge: 300_000 }
    );
  };

  // Chip strip — one chip per active filter. Multi-value params
  // (condition, attr.*) still render as a single chip with a comma-joined
  // label so removing clears the whole facet at once, matching legacy UX.
  const chips = useMemo(() => {
    const list = [];
    const catName = categories.flatMap((c) => [c, ...(c.children || [])])
      .find((c) => c.slug === categorySlug)?.name;
    if (params.get("q")) list.push({ key: "q", label: `"${params.get("q")}"` });
    if (categorySlug) list.push({ key: "category", label: catName || categorySlug });
    if (params.get("region")) list.push({ key: "region", label: params.get("region") });
    if (params.get("city")) list.push({ key: "city", label: params.get("city") });
    if (params.get("area")) list.push({ key: "area", label: params.get("area") });
    if (params.get("min_price")) list.push({ key: "min_price", label: `≥ $${params.get("min_price")}` });
    if (params.get("max_price")) list.push({ key: "max_price", label: `≤ $${params.get("max_price")}` });
    if (params.get("make")) list.push({ key: "make", label: `Make: ${params.get("make")}` });
    if (params.get("model")) list.push({ key: "model", label: `Model: ${params.get("model")}` });
    if (params.get("min_year") || params.get("max_year")) {
      const y = `${params.get("min_year") || "…"}–${params.get("max_year") || "…"}`;
      list.push({ key: "__year__", label: `Year ${y}` });
    }
    if (params.get("condition")) list.push({ key: "condition", label: `Condition: ${params.get("condition")}` });
    params.forEach((v, k) => {
      if (k.startsWith("attr.") && v) {
        const name = k.slice(5);
        // Iter-374 — friendlier labels for the N+ csv refinements
        if (["Bedrooms", "Bathrooms", "Car Parking"].includes(name)) {
          const n = v.split(",")[0].replace("+", "");
          list.push({ key: k, label: `${n}+ ${name.toLowerCase()}` });
        } else {
          list.push({ key: k, label: `${name}: ${v}` });
        }
      }
    });
    if (params.get("sort") === "distance" && params.get("lat") && params.get("lng")) {
      list.push({ key: "__nearest__", label: "Nearest first" });
    }
    const oh = params.get("open_home");
    if (oh === "weekend") list.push({ key: "open_home", label: "Open home this weekend" });
    else if (oh === "yes") list.push({ key: "open_home", label: "Has open home" });
    return list;
  }, [params, categories, categorySlug]);

  const removeChip = (key) => {
    const next = new URLSearchParams(params);
    next.delete("page"); // Iter-396 — filter change resets to page 1
    if (key === "__year__") {
      next.delete("min_year");
      next.delete("max_year");
    } else if (key === "__nearest__") {
      next.delete("sort");
      next.delete("lat");
      next.delete("lng");
    } else if (key === "make") {
      // Model is scoped to a make — removing Make must also drop Model so
      // we don't leave an orphan filter in the URL/request (Iter-238 RCA).
      next.delete("make");
      next.delete("model");
    } else {
      next.delete(key);
    }
    setParams(next);
  };

  const clearAll = () => {
    // Retain the category chip so users stay inside the category page
    // context; wipe everything else.
    const keep = new URLSearchParams();
    if (categorySlug) keep.set("category", categorySlug);
    setParams(keep);
  };

  const categoryName = useMemo(
    () =>
      categories
        .flatMap((c) => [c, ...(c.children || [])])
        .find((c) => c.slug === categorySlug)?.name,
    [categories, categorySlug]
  );

  const total = fetch.data?.total;
  const seoTitle = useMemo(
    () => composeSeoTitle(params, categoryName, total),
    [params, categoryName, total]
  );
  const seoDescription = useMemo(() => {
    // Description mirrors the title composition. Strip a leading "Browse "
    // from the empty-fallback lead so we don't emit "Browse browse
    // classifieds…" (iteration_63 RCA).
    const rawLead = composeSeoTitle(params, categoryName);
    const lead = rawLead.replace(/^Browse\s+/i, "").toLowerCase();
    const region = params.get("region");
    const suffix = region
      ? `Browse ${lead} on Allsale Classified — New Zealand's premium classifieds site.`
      : `Browse ${lead} across New Zealand on Allsale Classified. Post your ad free.`;
    return suffix.slice(0, 240);
  }, [params, categoryName]);

  // JSON-LD CollectionPage schema mirrors the client meta so both crawlers
  // and rich-result eligibility stay in sync with the active filters.
  // `name` must EXACTLY equal og:title (the branded fullTitle emitted by
  // Seo), per iteration_63 acceptance criteria.
  const brandedTitle = `${seoTitle} · Allsale Classified`;
  const jsonLd = useMemo(() => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: brandedTitle,
      description: seoDescription,
      url:
        typeof window !== "undefined"
          ? window.location.href
          : `${origin}/search`,
      isPartOf: {
        "@type": "WebSite",
        name: "Allsale Classified",
        url: origin || "https://www.allsale.co.nz",
      },
      about: categoryName ? { "@type": "Thing", name: categoryName } : undefined,
    };
  }, [brandedTitle, seoDescription, categoryName]);

  return (
    <div data-testid={SEARCH.root} className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo
        title={seoTitle}
        description={seoDescription}
        type="website"
        jsonLd={jsonLd}
        // WO 800 §2 — a single-category search page canonicalises to the
        // category's own URL (the one in sitemap-categories.xml), never to
        // /search?category=…; without a category it keeps today's self URL.
        url={categorySlug && typeof window !== "undefined" ? `${window.location.origin}/c/${categorySlug}` : undefined}
      />
      <Breadcrumbs items={[{ label: "Search" }]} />

      {/* Iter-250 §2: When the user lands on /search with no category
        chosen (the "See All" landing from the Home tile section), show
        every root category as a photo/emoji tile so browsing stays
        visual. Skips itself once a category filter is active. */}
      {!categorySlug && categories.length > 0 && (
        <section
          className="mt-4"
          aria-label="Browse by category"
          data-testid="search-browse-categories"
        >
          <h2 className="font-display text-sm font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">
            Browse by category
          </h2>
          <ul className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {categories.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/c/${c.slug}`}
                  data-testid={`search-browse-tile-${c.slug}`}
                  className="group flex flex-col items-center gap-2"
                >
                  <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white ring-2 ring-[color:var(--brand-blue-wash)] group-hover:ring-[color:var(--brand-orange)] shadow-card overflow-hidden grid place-items-center text-[color:var(--brand-blue)] transition-[box-shadow,transform,ring-color] duration-300 ease-out group-hover:-translate-y-0.5">
                    <CategoryIcon
                      name={c.icon}
                      thumbnailUrl={c.thumbnail_url}
                      emoji={c.emoji}
                      alt={c.name}
                      className={c.thumbnail_url ? "w-full h-full" : "w-7 h-7"}
                    />
                  </span>
                  <span className="text-xs font-semibold text-slate-700 text-center leading-tight group-hover:text-[color:var(--brand-blue)] clamp-2">
                    {c.name}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">
          {params.get("q") ? (
            <>Results for <span className="text-[color:var(--brand-blue)]">&ldquo;{params.get("q")}&rdquo;</span></>
          ) : (
            "Browse all listings"
          )}
        </h1>
        <SaveSearchButton
          criteria={{
            q: params.get("q") || "",
            category_slug: categorySlug,
            region: params.get("region") || "",
            city: params.get("city") || "",
            min_price: params.get("min_price") || "",
            max_price: params.get("max_price") || "",
          }}
        />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <div className="text-sm text-slate-500" data-testid={SEARCH.resultsCount}>
          {fetch.isLoading
            ? "Loading…"
            : `${fetch.data?.total ?? 0} ${
                categoryName
                  ? categoryName.toLowerCase()
                  : fetch.data?.total === 1
                  ? "listing"
                  : "listings"
              }`}{" "}
          in New Zealand
        </div>
        {/* Iter-372 — property-only open-home toggle chip */}
        {isPropertyCat && (
          <button
            type="button"
            aria-pressed={openHome === "weekend"}
            data-testid="search-open-home-chip"
            onClick={() => applyFilters({ open_home: openHome === "weekend" ? "" : "weekend" })}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
              openHome === "weekend"
                ? "bg-[color:var(--brand-blue)] text-white border-transparent"
                : "bg-white text-slate-600 border-soft hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)]"
            }`}
          >
            Open home this weekend
          </button>
        )}
        {/* Iter-356 — contextual distance sort */}
        {nearestActive ? (
          <span data-testid="search-nearest-active" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
            <MapPin className="w-3 h-3" /> Nearest first
          </span>
        ) : geoState === "denied" ? (
          <span data-testid="search-nearest-denied" className="text-xs font-medium text-slate-400">
            Location unavailable — keeping current sort
          </span>
        ) : (
          <button
            type="button"
            onClick={sortNearest}
            disabled={geoState === "locating"}
            data-testid="search-sort-nearest-btn"
            className="inline-flex items-center gap-1 rounded-full border border-soft bg-white px-3 py-1 text-xs font-semibold text-slate-600 hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)] transition-colors disabled:opacity-60"
          >
            {geoState === "locating" ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" /> Locating…
              </>
            ) : (
              <>
                <MapPin className="w-3 h-3" /> Sort by nearest
              </>
            )}
          </button>
        )}
        {/* Iter-432 — right-aligned sort dropdown (all categories) */}
        <SortDropdown
          value={params.get("sort") || ""}
          onChange={(v) => applyFilters({ sort: v })}
          categorySlug={params.get("category") || ""}
        />
      </div>

      {/* Iter-374 — TradeMe-style Refine rail + panel (Property only) */}
      {isPropertyCat && (
        <PropertyRefine
          params={params}
          applyFilters={applyFilters}
          total={fetch.data?.total}
          loading={fetch.isLoading}
        />
      )}

      {chips.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="active-filter-chips">
          {chips.map((c) => (
            <button
              key={c.key}
              onClick={() => removeChip(c.key)}
              data-testid={`chip-${c.key}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-soft px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)] transition-colors"
            >
              {c.label} <X className="w-3 h-3" />
            </button>
          ))}
          <button
            type="button"
            onClick={clearAll}
            data-testid="clear-all-chips"
            className="inline-flex items-center gap-1.5 rounded-full border border-transparent text-xs font-semibold text-red-500 hover:bg-red-50 px-3 py-1.5 transition-colors"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="mt-8 flex flex-col lg:flex-row gap-8">
        <SchemaFilterRail
          params={params}
          categorySlug={categorySlug}
          onApply={applyFilters}
        />
        <div className="flex-1 min-w-0">
          {/* Iter-538 — quick filter chip bar (second entry point into the
              Refine rail; clearing a chip refetches immediately). */}
          <div className="mb-4">
            <QuickChips params={params} categorySlug={categorySlug} onClear={applyFilters} />
          </div>
          {/* Iter-372 — dedicated zero-state for the weekend filter */}
          {!fetch.isLoading && openHome === "weekend" && (fetch.data?.listings || []).length === 0 ? (
            <div className="rounded-2xl bg-white shadow-card p-10 text-center" data-testid="open-home-zero-state">
              <h2 className="font-display text-xl font-bold text-slate-900">No open homes this weekend</h2>
              <p className="mt-1 text-sm text-slate-500">New times are usually posted by Friday.</p>
              <button
                type="button"
                onClick={() => applyFilters({ open_home: "yes" })}
                data-testid="open-home-see-all"
                className="mt-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-5 py-2 transition-colors"
              >
                See all open homes
              </button>
            </div>
          ) : !fetch.isLoading &&
            isPropertyCat &&
            (fetch.data?.listings || []).length === 0 &&
            chips.some((c) => c.key !== "category") ? (
            /* Iter-374 — refine zero-state with one-tap removal chips */
            <div className="rounded-2xl bg-white shadow-card p-10 text-center" data-testid="refine-zero-state">
              <h2 className="font-display text-xl font-bold text-slate-900">No properties match</h2>
              <p className="mt-1 text-sm text-slate-500">Try removing a refinement:</p>
              <div className="mt-3 flex flex-wrap justify-center gap-2">
                {chips
                  .filter((c) => c.key !== "category")
                  .map((c) => (
                    <button
                      key={c.key}
                      onClick={() => removeChip(c.key)}
                      data-testid={`refine-zero-chip-${c.key}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white border border-soft px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-red-400 hover:text-red-600 transition-colors"
                    >
                      {c.label} <X className="w-3 h-3" />
                    </button>
                  ))}
              </div>
            </div>
          ) : (
            <>
              <ListingGrid
                loading={fetch.isLoading}
                listings={fetch.data?.listings || []}
                empty="No listings match your filters."
                showOpenHome={!!openHome}
              />
              <Pagination
                page={page}
                total={fetch.data?.total ?? 0}
                limit={PAGE_SIZE}
                onPage={goToPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
